import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Star, LogOut, Search, Filter, Send, X, BadgeCheck, MapPin, Users, TrendingUp, Briefcase, Instagram, Youtube, Play, Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import StarAvatarFallback from "@/components/StarAvatarFallback";
import { Badge } from "@/components/ui/badge";
import BottomNav from "@/components/BottomNav";
import InfluencerCard from "@/components/InfluencerCard";
import { ProUpgradePrompt } from "@/components/BrandSubscriptionCard";
import { useAuth, API } from "@/App";

const BrandDiscover = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [influencers, setInfluencers] = useState([]);
  const [topInfluencers, setTopInfluencers] = useState([]);
  const [topInfluencerImages, setTopInfluencerImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  const [selectedInfluencerImageUrl, setSelectedInfluencerImageUrl] = useState(null);
  const [collaborateMessage, setCollaborateMessage] = useState("");
  const [dealAmount, setDealAmount] = useState("");
  const [sendingRequest, setSendingRequest] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showCollabModal, setShowCollabModal] = useState(false);
  const [upgradePrompt, setUpgradePrompt] = useState({ open: false, used: 3, limit: 3 });
  
  const [filters, setFilters] = useState({
    search: "",
    niche: "",
    location: "",
    city: "",
    platform: "",
    minFollowers: "",
    maxFollowers: "",
    minTrustScore: ""
  });
  const [portfolioData, setPortfolioData] = useState(null);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [profilePortfolio, setProfilePortfolio] = useState({ items: [] });
  const [loadingProfilePortfolio, setLoadingProfilePortfolio] = useState(false);

  const niches = ["Fashion", "Tech", "Food", "Fitness", "Beauty", "Travel", "Lifestyle", "Comedy", "Education", "Gaming"];
  const platforms = ["Instagram", "YouTube"];

  useEffect(() => {
    fetchInfluencers();
  }, []);

  // Load top influencer images
  useEffect(() => {
    const loadTopImages = async () => {
      const imageMap = {};
      for (const inf of topInfluencers) {
        if (inf.picture) {
          if (inf.picture.startsWith("http")) {
            imageMap[inf.user_id] = inf.picture;
          } else {
            try {
              const response = await axios.get(`${API}/files/${inf.picture}`, {
                responseType: 'blob'
              });
              imageMap[inf.user_id] = URL.createObjectURL(response.data);
            } catch (error) {
              imageMap[inf.user_id] = null;
            }
          }
        }
      }
      setTopInfluencerImages(imageMap);
    };
    if (topInfluencers.length > 0) {
      loadTopImages();
    }
  }, [topInfluencers]);

  // Load selected influencer image
  useEffect(() => {
    if (selectedInfluencer?.picture) {
      loadInfluencerImage(selectedInfluencer.picture);
    } else {
      setSelectedInfluencerImageUrl(null);
    }
  }, [selectedInfluencer]);

  const loadInfluencerImage = async (picturePath) => {
    if (picturePath.startsWith("http")) {
      setSelectedInfluencerImageUrl(picturePath);
    } else {
      try {
        const response = await axios.get(`${API}/files/${picturePath}`, {
          responseType: 'blob'
        });
        setSelectedInfluencerImageUrl(URL.createObjectURL(response.data));
      } catch (error) {
        setSelectedInfluencerImageUrl(null);
      }
    }
  };

  const fetchInfluencers = async (searchFilters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchFilters.search) params.append("search", searchFilters.search);
      if (searchFilters.niche) params.append("niche", searchFilters.niche);
      if (searchFilters.location) params.append("location", searchFilters.location);
      if (searchFilters.platform) params.append("platform", searchFilters.platform);
      if (searchFilters.minFollowers) params.append("min_followers", searchFilters.minFollowers);
      if (searchFilters.maxFollowers) params.append("max_followers", searchFilters.maxFollowers);
      if (searchFilters.minTrustScore) params.append("min_trust_score", searchFilters.minTrustScore);

      const response = await axios.get(`${API}/influencers?${params.toString()}`);
      setInfluencers(response.data);
      
      // Get top influencers (highest trust score, then followers)
      const sorted = [...response.data].sort((a, b) => {
        if ((b.trust_score || 0) !== (a.trust_score || 0)) {
          return (b.trust_score || 0) - (a.trust_score || 0);
        }
        return b.followers_count - a.followers_count;
      });
      setTopInfluencers(sorted.slice(0, 5));
    } catch (error) {
      toast.error("Failed to load influencers");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchInfluencers(filters);
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      niche: "",
      location: "",
      platform: "",
      minFollowers: "",
      maxFollowers: "",
      minTrustScore: ""
    });
    fetchInfluencers({});
  };

  const fetchPortfolio = async (userId) => {
    try {
      const response = await axios.get(`${API}/influencer/${userId}/portfolio`);
      setPortfolioData(response.data);
      setShowPortfolioModal(true);
    } catch (error) {
      toast.error("Failed to load portfolio");
    }
  };

  // Fetch portfolio when profile modal opens
  const fetchProfilePortfolio = async (userId) => {
    setLoadingProfilePortfolio(true);
    setProfilePortfolio({ items: [] });
    try {
      const response = await axios.get(`${API}/influencer/${userId}/portfolio`);
      setProfilePortfolio(response.data);
    } catch (error) {
      setProfilePortfolio({ items: [] });
    } finally {
      setLoadingProfilePortfolio(false);
    }
  };

  // Open profile modal with portfolio
  const openProfileWithPortfolio = (influencer) => {
    setSelectedInfluencer(influencer);
    setShowProfileModal(true);
    fetchProfilePortfolio(influencer.user_id);
  };

  // Helper to get correct media URL
  const getMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    // If URL already starts with /api/files, use API base URL without /api
    if (url.startsWith('/api/files/')) {
      return `${API.replace('/api', '')}${url}`;
    }
    return `${API.replace('/api', '')}/files/${url}`;
  };

  const handleCollaborate = async () => {
    if (!selectedInfluencer) return;
    
    setSendingRequest(true);
    try {
      await axios.post(`${API}/brand/collaborate`, {
        influencer_id: selectedInfluencer.user_id,
        message: collaborateMessage,
        deal_amount: dealAmount ? parseInt(dealAmount) : null
      }, { 
        
        headers: getAuthHeaders()
      });
      
      toast.success("Collaboration request sent!");
      setShowCollabModal(false);
      setSelectedInfluencer(null);
      setCollaborateMessage("");
      setDealAmount("");
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 402 && detail && typeof detail === "object" && detail.code === "BRAND_UPGRADE_REQUIRED") {
        setShowCollabModal(false);
        setUpgradePrompt({ open: true, used: detail.used ?? 3, limit: detail.limit ?? 3 });
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to send request"));
      }
    } finally {
      setSendingRequest(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  const formatFollowers = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-nav">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#87CEEB]/20 flex items-center justify-center">
              <Star className="w-4 h-4 text-[#FF69B4]" />
            </div>
            <span className="font-outfit font-bold text-lg text-[#1E293B]">Discover</span>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleLogout}
            className="text-[#64748B] hover:text-[#FF69B4]"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Search Bar */}
        <div className="bg-white rounded-2xl p-4 mb-6 shadow-sm">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
              <Input
                placeholder="Search influencers by name..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="pl-10 rounded-xl"
                data-testid="search-input"
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
              className={`rounded-xl ${showFilters ? "bg-[#FFF0F5] border-[#FF69B4]" : ""}`}
              data-testid="filter-toggle-btn"
            >
              <Filter className="w-5 h-5" />
            </Button>
            <Button
              onClick={handleSearch}
              className="bg-[#FF69B4] hover:bg-[#E05297] rounded-xl"
              data-testid="search-btn"
            >
              Search
            </Button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* Niche Filter */}
                <div>
                  <label className="text-sm text-[#64748B] mb-1 block">Niche</label>
                  <Select value={filters.niche || "all"} onValueChange={(v) => setFilters({...filters, niche: v === "all" ? "" : v})}>
                    <SelectTrigger className="rounded-xl" data-testid="filter-niche">
                      <SelectValue placeholder="All Niches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Niches</SelectItem>
                      {niches.map((n) => (
                        <SelectItem key={n} value={n}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Location Filter - Text Input for City Search */}
                <div>
                  <label className="text-sm text-[#64748B] mb-1 block flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    City/Location
                  </label>
                  <Input
                    type="text"
                    placeholder="Type city name..."
                    value={filters.location}
                    onChange={(e) => setFilters({...filters, location: e.target.value})}
                    className="rounded-xl"
                    data-testid="filter-location"
                  />
                </div>
                
                {/* Platform Filter */}
                <div>
                  <label className="text-sm text-[#64748B] mb-1 block">Platform</label>
                  <Select value={filters.platform || "all"} onValueChange={(v) => setFilters({...filters, platform: v === "all" ? "" : v})}>
                    <SelectTrigger className="rounded-xl" data-testid="filter-platform">
                      <SelectValue placeholder="All Platforms" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Platforms</SelectItem>
                      {platforms.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Min Followers */}
                <div>
                  <label className="text-sm text-[#64748B] mb-1 block">Min Followers</label>
                  <Input
                    type="number"
                    placeholder="e.g. 1000"
                    value={filters.minFollowers}
                    onChange={(e) => setFilters({...filters, minFollowers: e.target.value})}
                    className="rounded-xl"
                    data-testid="filter-min-followers"
                  />
                </div>
                
                {/* Max Followers */}
                <div>
                  <label className="text-sm text-[#64748B] mb-1 block">Max Followers</label>
                  <Input
                    type="number"
                    placeholder="e.g. 100000"
                    value={filters.maxFollowers}
                    onChange={(e) => setFilters({...filters, maxFollowers: e.target.value})}
                    className="rounded-xl"
                    data-testid="filter-max-followers"
                  />
                </div>
                
                {/* Min Engagement Rate */}
                <div>
                  <label className="text-sm text-[#64748B] mb-1 block">Min Trust Score</label>
                  <Select
                    value={filters.minTrustScore || "any"}
                    onValueChange={(value) => setFilters({...filters, minTrustScore: value === "any" ? "" : value})}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="filter-min-trust">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="3">3+ ⭐⭐⭐</SelectItem>
                      <SelectItem value="4">4+ ⭐⭐⭐⭐</SelectItem>
                      <SelectItem value="4.5">4.5+ ⭐⭐⭐⭐½</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Active Filters Display */}
              {(filters.niche || filters.location || filters.platform || filters.minFollowers || filters.maxFollowers || filters.minTrustScore) && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {filters.niche && (
                    <Badge className="bg-[#FFF0F5] text-[#FF69B4]">
                      Niche: {filters.niche}
                      <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, niche: ""})} />
                    </Badge>
                  )}
                  {filters.location && (
                    <Badge className="bg-[#F0F9FF] text-[#87CEEB]">
                      Location: {filters.location}
                      <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, location: ""})} />
                    </Badge>
                  )}
                  {filters.platform && (
                    <Badge className="bg-purple-50 text-purple-600">
                      Platform: {filters.platform}
                      <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, platform: ""})} />
                    </Badge>
                  )}
                  {filters.minFollowers && (
                    <Badge className="bg-green-50 text-green-600">
                      Min: {formatFollowers(filters.minFollowers)} followers
                      <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, minFollowers: ""})} />
                    </Badge>
                  )}
                  {filters.maxFollowers && (
                    <Badge className="bg-green-50 text-green-600">
                      Max: {formatFollowers(filters.maxFollowers)} followers
                      <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, maxFollowers: ""})} />
                    </Badge>
                  )}
                  {filters.minTrustScore && (
                    <Badge className="bg-yellow-50 text-yellow-600">
                      Trust: {filters.minTrustScore}+ ⭐
                      <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => setFilters({...filters, minTrustScore: ""})} />
                    </Badge>
                  )}
                </div>
              )}
              
              <div className="flex justify-end mt-4">
                <Button variant="ghost" onClick={clearFilters} className="text-[#64748B]" data-testid="clear-filters-btn">
                  <X className="w-4 h-4 mr-1" />
                  Clear All Filters
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Top Influencers Carousel */}
        {topInfluencers.length > 0 && (
          <div className="mb-6">
            <h2 className="font-outfit font-semibold text-lg text-[#1E293B] mb-3">Top Influencers</h2>
            <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-2">
              {topInfluencers.map((inf) => (
                <div 
                  key={inf.user_id}
                  className="flex-shrink-0 w-32 bg-white rounded-2xl p-4 text-center cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openProfileWithPortfolio(inf)}
                  data-testid={`top-influencer-${inf.user_id}`}
                >
                  <div className="relative inline-block mb-2">
                    <Avatar className="w-16 h-16 border-2 border-[#E2E8F0]">
                      <AvatarImage src={topInfluencerImages[inf.user_id] || inf.picture} alt={inf.name} />
                      <AvatarFallback className="p-0 bg-transparent overflow-hidden"><StarAvatarFallback iconClassName="w-7 h-7" /></AvatarFallback>
                    </Avatar>
                    {inf.is_verified && (
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#87CEEB] rounded-full flex items-center justify-center">
                        <BadgeCheck className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <p className="font-medium text-sm text-[#1E293B] truncate">{inf.name}</p>
                  <p className="text-xs text-[#64748B]">{formatFollowers(inf.followers_count)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Influencers Grid */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit font-semibold text-lg text-[#1E293B]">All Influencers</h2>
            <span className="text-sm text-[#64748B]">{influencers.length} found</span>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="discover-grid">
              {influencers.map((influencer) => (
                <InfluencerCard 
                  key={influencer.user_id} 
                  influencer={influencer}
                  showActions={true}
                  onViewProfile={() => openProfileWithPortfolio(influencer)}
                  onViewPortfolio={() => {
                    setSelectedInfluencer(influencer);
                    fetchPortfolio(influencer.user_id);
                  }}
                  onCollaborate={() => {
                    setSelectedInfluencer(influencer);
                    setCollaborateMessage("");
                    setShowCollabModal(true);
                  }}
                  hideProfileLinks={true}
                />
              ))}
            </div>
          )}

          {!loading && influencers.length === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl">
              <p className="text-[#64748B]">No influencers found matching your criteria</p>
              <Button variant="link" onClick={clearFilters} className="text-[#FF69B4]">Clear filters</Button>
            </div>
          )}
        </div>
      </main>

      {/* View Profile Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit">Influencer Profile</DialogTitle>
          </DialogHeader>
          
          {selectedInfluencer && (
            <div className="mt-4">
              {/* Profile Header */}
              <div className="flex flex-col items-center text-center mb-6">
                <div className="relative mb-3">
                  <Avatar className="w-24 h-24 border-4 border-[#E2E8F0]">
                    <AvatarImage src={selectedInfluencerImageUrl} alt={selectedInfluencer.name} />
                    <AvatarFallback className="p-0 bg-transparent overflow-hidden">
                      <StarAvatarFallback iconClassName="w-10 h-10" />
                    </AvatarFallback>
                  </Avatar>
                  {selectedInfluencer.is_verified && (
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#87CEEB] rounded-full flex items-center justify-center">
                      <BadgeCheck className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>
                <h3 className="font-outfit font-bold text-xl text-[#1E293B]">{selectedInfluencer.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="bg-[#FFF0F5] text-[#FF69B4] hover:bg-[#FFF0F5]">
                    {selectedInfluencer.niche || selectedInfluencer.niches?.[0]}
                  </Badge>
                  <span className="text-sm text-[#64748B] flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {selectedInfluencer.location}
                  </span>
                </div>
              </div>

              {/* Bio */}
              {selectedInfluencer.bio && (
                <div className="bg-[#F8FAFC] rounded-xl p-4 mb-4">
                  <p className="text-[#64748B] text-sm">{selectedInfluencer.bio}</p>
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-[#FFF0F5] rounded-xl p-4 text-center">
                  <Users className="w-5 h-5 text-[#FF69B4] mx-auto mb-1" />
                  <p className="font-outfit font-bold text-lg text-[#1E293B]">{formatFollowers(selectedInfluencer.followers_count)}</p>
                  <p className="text-xs text-[#64748B]">Followers</p>
                </div>
                <div className="bg-[#F0F9FF] rounded-xl p-4 text-center">
                  <Star className="w-5 h-5 text-yellow-500 mx-auto mb-1" />
                  <p className="font-outfit font-bold text-lg text-[#1E293B]">{selectedInfluencer.trust_score || 'New'}</p>
                  <p className="text-xs text-[#64748B]">Trust Score</p>
                </div>
                <div className="bg-[#FFF0F5] rounded-xl p-4 text-center">
                  <Briefcase className="w-5 h-5 text-[#FF69B4] mx-auto mb-1" />
                  <p className="font-outfit font-bold text-lg text-[#1E293B]">{selectedInfluencer.completed_deals || 0}</p>
                  <p className="text-xs text-[#64748B]">Deals Done</p>
                </div>
                <div className="bg-[#F0F9FF] rounded-xl p-4 text-center">
                  {selectedInfluencer.platforms?.includes("YouTube") || selectedInfluencer.platform === "YouTube" ? (
                    <Youtube className="w-5 h-5 text-red-500 mx-auto mb-1" />
                  ) : (
                    <Instagram className="w-5 h-5 text-pink-500 mx-auto mb-1" />
                  )}
                  <p className="font-outfit font-bold text-lg text-[#1E293B]">
                    {selectedInfluencer.platforms?.length > 1 ? 'Multi' : selectedInfluencer.platform || 'Instagram'}
                  </p>
                  <p className="text-xs text-[#64748B]">Platform</p>
                </div>
              </div>

              {/* Portfolio Section */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-outfit font-semibold text-[#1E293B] flex items-center gap-2">
                    <Image className="w-4 h-4 text-[#FF69B4]" />
                    Portfolio & Work
                  </h4>
                  {profilePortfolio.items?.length > 0 && (
                    <span className="text-xs text-[#64748B]">{profilePortfolio.items.length} items</span>
                  )}
                </div>
                
                {loadingProfilePortfolio ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="w-6 h-6 border-2 border-[#FF69B4] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : profilePortfolio.items?.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {profilePortfolio.items.slice(0, 6).map((item, index) => (
                      <div 
                        key={item.item_id || index}
                        className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group"
                        onClick={() => window.open(getMediaUrl(item.url), '_blank')}
                      >
                        {item.type === 'video' ? (
                          <>
                            <video 
                              src={getMediaUrl(item.url)}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                              <div className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                                <Play className="w-4 h-4 text-[#FF69B4] ml-0.5" />
                              </div>
                            </div>
                          </>
                        ) : (
                          <img 
                            src={getMediaUrl(item.url)}
                            alt={item.caption || 'Portfolio'} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-[#F8FAFC] rounded-xl p-4 text-center">
                    <Image className="w-8 h-8 text-[#E2E8F0] mx-auto mb-2" />
                    <p className="text-sm text-[#94A3B8]">No portfolio items yet</p>
                  </div>
                )}
              </div>

              {/* Info about profile links */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                <p className="text-sm text-yellow-800 text-center">
                  Social media links will be shared after collaboration is accepted.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowProfileModal(false)}
                  className="flex-1 rounded-full"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setShowProfileModal(false);
                    setCollaborateMessage("");
                    setShowCollabModal(true);
                  }}
                  className="flex-1 bg-[#FF69B4] hover:bg-[#E05297] rounded-full"
                  data-testid="modal-collab-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Request
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Collaborate Dialog */}
      <Dialog open={showCollabModal} onOpenChange={setShowCollabModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Send Collaboration Request</DialogTitle>
          </DialogHeader>
          
          {selectedInfluencer && (
            <div className="mt-4">
              {/* Influencer Preview */}
              <div className="flex items-center gap-4 p-4 bg-[#F8FAFC] rounded-xl mb-4">
                <Avatar className="w-14 h-14">
                  <AvatarImage src={selectedInfluencerImageUrl} />
                  <AvatarFallback className="p-0 bg-transparent overflow-hidden">
                    <StarAvatarFallback iconClassName="w-6 h-6" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#1E293B]">{selectedInfluencer.name}</span>
                    {selectedInfluencer.is_verified && (
                      <BadgeCheck className="w-4 h-4 text-[#87CEEB]" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#64748B]">
                    <Badge variant="secondary" className="bg-[#FFF0F5] text-[#FF69B4]">
                      {selectedInfluencer.niche || selectedInfluencer.niches?.[0]}
                    </Badge>
                    <span>{formatFollowers(selectedInfluencer.followers_count)} followers</span>
                  </div>
                </div>
              </div>

              {/* Message Input */}
              <div className="mb-4">
                <label className="text-sm text-[#64748B] mb-1 block">Your Message (Optional)</label>
                <Textarea
                  placeholder="Hi! We'd love to collaborate with you on..."
                  value={collaborateMessage}
                  onChange={(e) => setCollaborateMessage(e.target.value)}
                  className="rounded-xl min-h-[100px]"
                  data-testid="collab-message-input"
                />
              </div>

              {/* Deal Amount Input */}
              <div className="mb-4">
                <label className="text-sm text-[#64748B] mb-1 block">Deal Amount (₹) - Optional</label>
                <Input
                  type="number"
                  placeholder="Enter deal amount (e.g., 5000)"
                  value={dealAmount}
                  onChange={(e) => setDealAmount(e.target.value)}
                  className="rounded-xl"
                  data-testid="deal-amount-input"
                />
                <p className="text-xs text-[#94A3B8] mt-1">
                  If amount is specified, influencer will see it when accepting. Payment will be processed via escrow.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowCollabModal(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCollaborate}
                  disabled={sendingRequest}
                  className="flex-1 bg-[#FF69B4] hover:bg-[#E05297] rounded-full"
                  data-testid="send-collab-btn"
                >
                  {sendingRequest ? "Sending..." : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Request
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Portfolio Modal */}
      <Dialog open={showPortfolioModal} onOpenChange={setShowPortfolioModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {selectedInfluencer?.name}'s Portfolio
            </DialogTitle>
          </DialogHeader>
          {portfolioData && (
            <div className="mt-4">
              {portfolioData.items && portfolioData.items.length > 0 ? (
                <div className="grid grid-cols-2 gap-4">
                  {portfolioData.items.map((item) => (
                    <div key={item.item_id} className="rounded-xl overflow-hidden border border-[#E2E8F0]">
                      {item.type === 'image' ? (
                        <img 
                          src={`${API.replace('/api', '')}${item.url}`}
                          alt={item.title || 'Portfolio item'}
                          className="w-full h-48 object-cover cursor-pointer hover:opacity-90"
                          onClick={() => window.open(`${API.replace('/api', '')}${item.url}`, '_blank')}
                        />
                      ) : (
                        <video 
                          src={`${API.replace('/api', '')}${item.url}`}
                          controls
                          className="w-full h-48 object-cover"
                        />
                      )}
                      {(item.title || item.description) && (
                        <div className="p-3">
                          {item.title && <p className="font-medium text-sm text-[#1E293B]">{item.title}</p>}
                          {item.description && <p className="text-xs text-[#64748B]">{item.description}</p>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-[#64748B]">No portfolio items yet</p>
                  <p className="text-sm text-[#94A3B8]">This influencer hasn't uploaded any work samples</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BottomNav userType="brand" />

      <ProUpgradePrompt
        open={upgradePrompt.open}
        used={upgradePrompt.used}
        limit={upgradePrompt.limit}
        onClose={() => setUpgradePrompt({ ...upgradePrompt, open: false })}
        onUpgrade={() => {
          setUpgradePrompt({ ...upgradePrompt, open: false });
          navigate("/brand/profile");
        }}
      />
    </div>
  );
};

export default BrandDiscover;
